import { apiClient } from "./client";
import { openBlobInNewTab } from "../lib/openBlobInNewTab";
//updated on 2024-06-10
import type {
  CategoryReviewPayload,
  CategoryReviewStagePayload,
  HODReviewPayload,
  RequisitionCategoryAccountCodeOption,
  RequisitionCloneSourceResponse,
  RequisitionComment,
  RequisitionCommentCreatePayload,
  RequisitionCreatePayload,
  RequisitionDetail,
  RequisitionDispute,
  RequisitionDisputeCreatePayload,
  RequisitionListResponse,
  ReturnedDraftRejectPayload,
  RequisitionUpdatePayload,
} from "../types/requisition";

interface DataEnvelope<TData> {
  data: TData;
}

interface RequisitionCategoryAccountCodeResponse {
  data: RequisitionCategoryAccountCodeOption[];
}

interface RequisitionPurposeSuggestionsResponse {
  data: string[];
}

interface RequisitionCloneSourceQueryParams {
  search?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

const REQUISITION_WRITE_TIMEOUT_MS = 120000;

function downloadBlob(blob: Blob, filename: string): void {
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(downloadUrl);
}

function wrapDataEnvelope<TData>(payload: DataEnvelope<TData> | TData): DataEnvelope<TData> {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "data" in payload
  ) {
    return payload as DataEnvelope<TData>;
  }

  return { data: payload as TData };
}

function hasAttachmentFiles(payload: RequisitionCreatePayload | RequisitionUpdatePayload): boolean {
  if (Array.isArray(payload.attachment_files) && payload.attachment_files.length > 0) {
    return true;
  }

  if (!("items" in payload) || !Array.isArray(payload.items)) {
    return false;
  }

  return payload.items.some(
    (item) => Array.isArray(item.item_attachment_files) && item.item_attachment_files.length > 0,
  );
}

function buildRequisitionFormData(payload: RequisitionCreatePayload | RequisitionUpdatePayload): FormData {
  const formData = new globalThis.FormData();

  if ("vessel_id" in payload && payload.vessel_id !== undefined && payload.vessel_id !== null) {
    formData.set("vessel_id", String(payload.vessel_id));
  }
  if ("origin" in payload && payload.origin !== undefined) {
    formData.set("origin", payload.origin);
  }
  if (
    "department" in payload &&
    payload.department !== undefined &&
    payload.department !== null
  ) {
    formData.set("department", payload.department);
  }
  if ("req_type" in payload && payload.req_type !== undefined) {
    formData.set("req_type", payload.req_type);
  }
  if ("priority" in payload && payload.priority !== undefined) {
    formData.set("priority", payload.priority);
  }
  if ("category_account_code_id" in payload && payload.category_account_code_id !== undefined) {
    formData.set("category_account_code_id", payload.category_account_code_id ?? "");
  }
  if ("purpose" in payload && payload.purpose !== undefined) {
    formData.set("purpose", payload.purpose);
  }
  if ("items" in payload && payload.items !== undefined) {
    formData.set(
      "items_json",
      JSON.stringify(
        payload.items.map(({ item_attachment_files: _itemAttachmentFiles, attachments: _attachments, ...item }) => item),
      ),
    );
    payload.items.forEach((item, index) => {
      const attachmentKey = item.attachment_key?.trim();
      const itemAttachmentFiles = item.item_attachment_files ?? [];
      if (!attachmentKey || itemAttachmentFiles.length === 0) {
        return;
      }
      itemAttachmentFiles.forEach((file) => {
        formData.append(`item_attachment_files__${attachmentKey}`, file);
      });
    });
  }
  if (payload.attachment_files) {
    payload.attachment_files.forEach((file) => {
      formData.append("attachment_files", file);
    });
  }

  return formData;
}

function hasCommentAttachmentFiles(payload: RequisitionCommentCreatePayload): boolean {
  return Array.isArray(payload.attachment_files) && payload.attachment_files.length > 0;
}

function buildCommentFormData(payload: RequisitionCommentCreatePayload): FormData {
  const formData = new globalThis.FormData();
  formData.set("text", payload.text);
  if (payload.parent_id !== undefined && payload.parent_id !== null) {
    formData.set("parent_id", payload.parent_id);
  }
  if (payload.client_comment_id) {
    formData.set("client_comment_id", payload.client_comment_id);
  }
  payload.attachment_files?.forEach((file) => {
    formData.append("attachment_files", file);
  });
  return formData;
}

interface RequisitionListQueryParams {
  filter?: string;
  status?: string;
  department?: string;
  priority?: string;
  purpose?: string;
  category_account_code_id?: string;
  req_type?: string;
  origin?: string;
  vessel_id?: string;
  date_from?: string;
  date_to?: string;
  sort_by?: string;
  page?: number;
  page_size?: number;
}

const REQUISITION_SORT_VALUES = new Set([
  "created_desc",
  "created_asc",
  "req_number_asc",
  "req_number_desc",
  "purpose_asc",
  "purpose_desc",
]);

function normalizeSortBy(sortBy: string | undefined): string {
  if (sortBy && REQUISITION_SORT_VALUES.has(sortBy)) {
    return sortBy;
  }
  return "created_desc";
}

function compareNullableText(left: string | null | undefined, right: string | null | undefined): number {
  return String(left ?? "").localeCompare(String(right ?? ""), undefined, { sensitivity: "base" });
}

function createdTime(row: { created_at?: string | null }): number {
  const parsed = Date.parse(row.created_at ?? "");
  return Number.isNaN(parsed) ? 0 : parsed;
}

function isWithinDateRange(row: { created_at?: string | null }, dateFrom?: string, dateTo?: string): boolean {
  if (!dateFrom && !dateTo) {
    return true;
  }
  const createdDate = row.created_at?.slice(0, 10);
  if (!createdDate) {
    return false;
  }
  if (dateFrom && createdDate < dateFrom) {
    return false;
  }
  if (dateTo && createdDate > dateTo) {
    return false;
  }
  return true;
}

function compareRequisitions(
  left: { id: string; req_number?: string | null; purpose?: string | null; created_at?: string | null },
  right: { id: string; req_number?: string | null; purpose?: string | null; created_at?: string | null },
  sortBy: string,
): number {
  switch (sortBy) {
    case "created_asc":
      return createdTime(left) - createdTime(right) || compareNullableText(left.id, right.id);
    case "req_number_asc":
      return compareNullableText(left.req_number, right.req_number) || createdTime(left) - createdTime(right);
    case "req_number_desc":
      return compareNullableText(right.req_number, left.req_number) || createdTime(right) - createdTime(left);
    case "purpose_asc":
      return compareNullableText(left.purpose, right.purpose) || createdTime(left) - createdTime(right);
    case "purpose_desc":
      return compareNullableText(right.purpose, left.purpose) || createdTime(right) - createdTime(left);
    case "created_desc":
    default:
      return createdTime(right) - createdTime(left) || compareNullableText(right.id, left.id);
  }
}

export async function listRequisitions(
  params: RequisitionListQueryParams = {},
): Promise<RequisitionListResponse> {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    const { listCachedRequisitions } = await import("../pwa/indexedDB/cacheStore");
    const cachedRecords = await listCachedRequisitions();
    const search = (params.purpose ?? params.filter ?? "").trim().toLowerCase();
    const sortBy = normalizeSortBy(params.sort_by);
    const rows = cachedRecords
      .map((record) => record.data)
      .filter((row) => (params.status ? row.status === params.status : true))
      .filter((row) => (params.department ? row.department === params.department : true))
      .filter((row) => (params.priority ? row.priority === params.priority : true))
      .filter((row) => (params.req_type ? row.req_type === params.req_type : true))
      .filter((row) => (params.origin ? row.origin === params.origin : true))
      .filter((row) => (params.vessel_id ? String(row.vessel_id) === String(params.vessel_id) : true))
      .filter((row) => isWithinDateRange(row, params.date_from, params.date_to))
      .filter((row) => {
        if (!search) {
          return true;
        }
        return [row.req_number, row.purpose, row.department, row.req_type]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(search);
      })
      .sort((left, right) => compareRequisitions(left, right, sortBy));
    return {
      data: rows,
      pagination: {
        page: 1,
        page_size: rows.length,
        total_count: rows.length,
        total_pages: 1,
      },
    };
  }

  const response = await apiClient.get<RequisitionListResponse>("/purchase/requisitions/", { params });
  return response.data;
}

export async function getRequisition(requisitionId: string): Promise<DataEnvelope<RequisitionDetail>> {
  const response = await apiClient.get<DataEnvelope<RequisitionDetail> | RequisitionDetail>(
    `/purchase/requisitions/${requisitionId}/`,
  );
  return wrapDataEnvelope(response.data);
}

export async function listRequisitionCategoryAccountCodes(): Promise<RequisitionCategoryAccountCodeResponse> {
  const response = await apiClient.get<RequisitionCategoryAccountCodeResponse>(
    "/purchase/requisitions/account-code-categories/",
  );
  return response.data;
}

export async function listRequisitionPurposeSuggestions(search: string): Promise<RequisitionPurposeSuggestionsResponse> {
  const response = await apiClient.get<RequisitionPurposeSuggestionsResponse>(
    "/purchase/requisitions/purpose-suggestions/",
    { params: { search } },
  );
  return response.data;
}

export async function listRequisitionCloneSources(
  params: RequisitionCloneSourceQueryParams = {},
): Promise<RequisitionCloneSourceResponse> {
  const response = await apiClient.get<RequisitionCloneSourceResponse>(
    "/purchase/requisitions/clone-sources/",
    { params },
  );
  return response.data;
}

export async function createRequisition(
  payload: RequisitionCreatePayload,
): Promise<DataEnvelope<RequisitionDetail>> {
  const requestBody = hasAttachmentFiles(payload) ? buildRequisitionFormData(payload) : payload;
  const response = await apiClient.post<DataEnvelope<RequisitionDetail>>("/purchase/requisitions/", requestBody, {
    timeout: REQUISITION_WRITE_TIMEOUT_MS,
  });
  return response.data;
}

export async function cloneRequisition(requisitionId: string): Promise<DataEnvelope<RequisitionDetail>> {
  const response = await apiClient.post<DataEnvelope<RequisitionDetail>>(
    `/purchase/requisitions/${requisitionId}/clone/`,
    {},
    {
      timeout: REQUISITION_WRITE_TIMEOUT_MS,
    },
  );
  return response.data;
}

export async function updateRequisition(
  requisitionId: string,
  payload: RequisitionUpdatePayload,
): Promise<DataEnvelope<RequisitionDetail>> {
  const requestBody = hasAttachmentFiles(payload) ? buildRequisitionFormData(payload) : payload;
  const response = await apiClient.patch<DataEnvelope<RequisitionDetail>>(
    `/purchase/requisitions/${requisitionId}/`,
    requestBody,
    {
      timeout: REQUISITION_WRITE_TIMEOUT_MS,
    },
  );
  return response.data;
}

async function fetchRequisitionAttachmentBlob(requisitionId: string, attachmentId: string): Promise<Blob> {
  const response = await apiClient.get<Blob>(
    `/purchase/requisitions/${requisitionId}/attachments/${attachmentId}/download/`,
    {
      responseType: "blob",
    },
  );
  return response.data;
}

export async function openRequisitionAttachment(requisitionId: string, attachmentId: string): Promise<void> {
  openBlobInNewTab(await fetchRequisitionAttachmentBlob(requisitionId, attachmentId));
}

export async function downloadRequisitionAttachment(
  requisitionId: string,
  attachmentId: string,
  originalFilename: string,
): Promise<void> {
  downloadBlob(await fetchRequisitionAttachmentBlob(requisitionId, attachmentId), originalFilename);
}

export async function removeRequisitionAttachment(
  requisitionId: string,
  attachmentId: string,
): Promise<DataEnvelope<RequisitionDetail>> {
  const response = await apiClient.delete<DataEnvelope<RequisitionDetail>>(
    `/purchase/requisitions/${requisitionId}/attachments/${attachmentId}/`,
  );
  return response.data;
}

export async function submitRequisition(requisitionId: string): Promise<DataEnvelope<RequisitionDetail>> {
  const response = await apiClient.post<DataEnvelope<RequisitionDetail>>(
    `/purchase/requisitions/${requisitionId}/submit/`,
    {},
  );
  return response.data;
}

export async function hodReviewRequisition(
  requisitionId: string,
  payload: HODReviewPayload,
): Promise<DataEnvelope<RequisitionDetail>> {
  const response = await apiClient.post<DataEnvelope<RequisitionDetail>>(
    `/purchase/requisitions/${requisitionId}/hod-review/`,
    payload,
  );
  return response.data;
}

export async function categoryReviewRequisition(
  requisitionId: string,
  payload: CategoryReviewPayload,
): Promise<DataEnvelope<RequisitionDetail>> {
  const response = await apiClient.post<DataEnvelope<RequisitionDetail>>(
    `/purchase/requisitions/${requisitionId}/category-review/`,
    payload,
  );
  return response.data;
}

export async function stageCategoryReviewRequisition(
  requisitionId: string,
  payload: CategoryReviewStagePayload,
): Promise<DataEnvelope<RequisitionDetail>> {
  const response = await apiClient.post<DataEnvelope<RequisitionDetail>>(
    `/purchase/requisitions/${requisitionId}/category-review/stage/`,
    payload,
  );
  return response.data;
}

export async function rejectReturnedDraftRequisition(
  requisitionId: string,
  payload: ReturnedDraftRejectPayload,
): Promise<DataEnvelope<RequisitionDetail>> {
  const response = await apiClient.post<DataEnvelope<RequisitionDetail>>(
    `/purchase/requisitions/${requisitionId}/reject-returned-draft/`,
    payload,
  );
  return response.data;
}

export async function cancelRequisition(
  requisitionId: string,
  reason: string,
): Promise<DataEnvelope<RequisitionDetail>> {
  const response = await apiClient.post<DataEnvelope<RequisitionDetail>>(
    `/purchase/requisitions/${requisitionId}/cancel/`,
    { reason },
  );
  return response.data;
}

export async function createRequisitionComment(
  requisitionId: string,
  payload: RequisitionCommentCreatePayload,
): Promise<DataEnvelope<RequisitionComment>> {
  const requestBody = hasCommentAttachmentFiles(payload) ? buildCommentFormData(payload) : payload;
  const response = await apiClient.post<DataEnvelope<RequisitionComment>>(
    `/purchase/requisitions/${requisitionId}/comments/`,
    requestBody,
  );
  return response.data;
}

function apiPathFromDownloadUrl(downloadUrl: string): string {
  return downloadUrl.startsWith("/api/") ? downloadUrl.slice(4) : downloadUrl;
}

async function fetchCommentAttachmentBlob(downloadUrl: string): Promise<Blob> {
  const response = await apiClient.get<Blob>(apiPathFromDownloadUrl(downloadUrl), {
    responseType: "blob",
  });
  return response.data;
}

export async function openCommentAttachment(downloadUrl: string): Promise<void> {
  openBlobInNewTab(await fetchCommentAttachmentBlob(downloadUrl));
}

export async function downloadCommentAttachment(downloadUrl: string, originalFilename: string): Promise<void> {
  downloadBlob(await fetchCommentAttachmentBlob(downloadUrl), originalFilename);
}

export async function listRequisitionDisputes(
  requisitionId: string,
): Promise<DataEnvelope<RequisitionDispute[]>> {
  const response = await apiClient.get<DataEnvelope<RequisitionDispute[]>>(
    `/purchase/requisitions/${requisitionId}/disputes/`,
  );
  return response.data;
}

export async function createRequisitionDispute(
  requisitionId: string,
  payload: RequisitionDisputeCreatePayload,
): Promise<DataEnvelope<RequisitionDispute>> {
  const response = await apiClient.post<DataEnvelope<RequisitionDispute>>(
    `/purchase/requisitions/${requisitionId}/disputes/`,
    payload,
  );
  return response.data;
}
